import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getVehiclePath } from "../Utils/vehicleUrls";

export default function DealerLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "overview";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const token = useMemo(() => localStorage.getItem("dealerToken"), []);
  const API = process.env.REACT_APP_API_BASE;

  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [autoFocusReply, setAutoFocusReply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Scroll container + sticky-to-bottom helpers
  const listRef = useRef(null);
  const shouldStickRef = useRef(true);
  const scrollDebounceRef = useRef(null);
  const NEAR_BOTTOM_PX = 120;

  // state for email send UX
  const [sendBusy, setSendBusy] = useState(false);
  const [sendOk, setSendOk] = useState(false);
  const [sendErr, setSendErr] = useState("");

  // optional: allow overriding recipient
  const [overrideEmail, setOverrideEmail] = useState("");

  // POST /api/dealer/leads/{id}/email-chat-link
  const sendChatLinkEmail = useCallback(async () => {
  setSendBusy(true);
  setSendErr("");
  const url = `${API}/dealer/leads/${id}/email-chat-link`;
  try {
    
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        toEmail: overrideEmail?.trim() || null,
      }),
    });

 
    const text = await res.text(); // read body even on error

    if (res.status === 401) {
      navigate("/dealer/login", { replace: true });
      return;
    }
    if (!res.ok) {
      // surface server error text to UI
      throw new Error(text || `HTTP ${res.status}`);
    }

    setSendOk(true);
    setTimeout(() => setSendOk(false), 2000);
  } catch (e) {
    console.error(e);
    setSendErr("Could not send email.");
    setTimeout(() => setSendErr(""), 2500);
  } finally {
    setSendBusy(false);
  }
}, [API, id, token, overrideEmail, navigate]);


  const isNearBottom = () => {
    const el = listRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  const scrollToBottom = (behavior = "smooth") => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  const onScroll = useCallback(() => {
    if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    scrollDebounceRef.current = setTimeout(() => {
      shouldStickRef.current = isNearBottom();
    }, 120);
  }, []);

  const unreadForDealer = lead?.unreadForDealer ?? lead?.UnreadForDealer ?? false;

  const normalizeMsgs = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((m) => ({
      id: m.id ?? m.Id ?? m.messageId ?? `tmp-${Math.random()}`,
      sender: (m.sender ?? m.Sender ?? "").toLowerCase(),
      body: m.body ?? m.Body ?? m.text ?? m.Text ?? "",
      createdAt:
        m.createdAt ?? m.CreatedAt ?? m.at ?? m.At ?? new Date().toISOString(),
      readAt: m.readAt ?? m.ReadAt ?? null,
    }));
  }, []);

  const markRead = useCallback(async () => {
    if (currentTab !== "chat" || !token) return;
    if (document.hidden) return;
    try {
      await fetch(`${API}/dealer/leads/${id}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  }, [API, id, token, currentTab]);

  const load = useCallback(async () => {
    if (!token) return navigate("/dealer/login", { replace: true });
    try {
      setLoading(true);
      setErr("");
      const res = await fetch(`${API}/dealer/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLead(data);
      if (Array.isArray(data.thread)) {
        setMessages(normalizeMsgs(data.thread));
      } else {
        setMessages([]);
      }
    } catch {
      setErr("Could not load lead.");
    } finally {
      setLoading(false);
    }
  }, [API, id, token, navigate, normalizeMsgs]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (currentTab === "chat") {
      const t = setTimeout(() => setAutoFocusReply(true), 0);
      return () => clearTimeout(t);
    }
  }, [currentTab]);

  useEffect(() => {
    if (currentTab !== "chat" || !token) return;
    setLead((prev) =>
      prev ? { ...prev, unreadForDealer: false, UnreadForDealer: false } : prev
    );
    fetch(`${API}/dealer/leads/${id}/read`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [currentTab, API, id, token]);

  // Attach debounced scroll listener when chat opens
  useEffect(() => {
    if (currentTab !== "chat") return;
    const el = listRef.current;
    if (!el) return;
    el.addEventListener("scroll", onScroll, { passive: true });
    // initialize stickiness on mount
    shouldStickRef.current = isNearBottom();
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    };
  }, [currentTab, onScroll]);

  useEffect(() => {
    if (currentTab !== "chat" || !token) return;

    let stop = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/dealer/leads/${id}/thread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const thread = await res.json();
          setMessages(normalizeMsgs(thread));
          // auto-scroll only if user is near bottom
          requestAnimationFrame(() => {
            if (shouldStickRef.current) scrollToBottom("smooth");
          });
        }
      } catch {}
      if (!stop) setTimeout(tick, 10000);
    };
    tick();
    return () => {
      stop = true;
    };
  }, [currentTab, API, id, token, normalizeMsgs]);

  useEffect(() => {
    if (currentTab !== "chat") return;
    markRead();
  }, [messages, currentTab, markRead]);

  useEffect(() => {
    if (currentTab !== "chat") return;
    const onFocusOrVis = () => markRead();
    window.addEventListener("focus", onFocusOrVis);
    document.addEventListener("visibilitychange", onFocusOrVis);
    return () => {
      window.removeEventListener("focus", onFocusOrVis);
      document.removeEventListener("visibilitychange", onFocusOrVis);
    };
  }, [currentTab, markRead]);

  useEffect(() => {
    const k = `dealer:lead:${id}:draft`;
    const saved = localStorage.getItem(k);
    if (saved) setReply(saved);
  }, [id]);

  useEffect(() => {
    const k = `dealer:lead:${id}:draft`;
    const t = setTimeout(() => {
      if (reply) localStorage.setItem(k, reply);
      else localStorage.removeItem(k);
    }, 300);
    return () => clearTimeout(t);
  }, [id, reply]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      const res = await fetch(`${API}/dealer/leads/${id}/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ body: reply.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = await res.json();
      const [norm] = normalizeMsgs([saved]);
      setMessages((m) => [...m, norm]);
      setReply("");
      markRead();
      // force stick after sending (expected UX)
      requestAnimationFrame(() => {
        shouldStickRef.current = true;
        scrollToBottom("smooth");
      });
    } catch {
      setErr("Could not send reply.");
      setTimeout(() => setErr(""), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-4xl mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/dealer/leads")}
            className="h-10 px-4 text-sm rounded-lg border border-gray-300 bg-gray-200 text-gray-700 hover:bg-gray-300"
          >
            ← Back to Leads
          </button>
          <h1 className="text-xl font-semibold">Lead #{id}</h1>
        </div>

        {err && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {err}
          </div>
        )}

        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : !lead ? (
          <div className="p-6 text-center text-gray-500">Lead not found.</div>
        ) : (
          <>
            {/* Tabs */}
            <div className="mb-4 flex gap-2">
              <button
                onClick={() => setTab("overview")}
                className={`px-3 py-2 rounded-lg border text-sm ${
                  currentTab === "overview"
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Overview
              </button>

              <button
                onClick={() => setTab("chat")}
                className={`relative px-3 py-2 rounded-lg border text-sm ${
                  currentTab === "chat"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Chat
                {unreadForDealer && currentTab !== "chat" && (
                  <span
                    className="absolute -top-1 -right-1 inline-block h-2.5 w-2.5 rounded-full bg-red-500"
                    title="New buyer message"
                  />
                )}
              </button>
            </div>

            {/* OVERVIEW TAB */}
            {currentTab === "overview" && (
              <div className="bg-white rounded-xl border p-4 mb-4">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-gray-500">Buyer</div>
                    <div className="font-medium">
                      {lead.buyerName || lead.buyerEmail || lead.buyerPhone || "—"}
                    </div>
                    {lead.buyerEmail && (
                      <div className="text-gray-600">{lead.buyerEmail}</div>
                    )}
                    {lead.buyerPhone && (
                      <div className="text-gray-600">{lead.buyerPhone}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-gray-500">Vehicle / Mestimate</div>
                    <div className="font-medium">
                      {lead.carId ? (
                        <a
                          href={getVehiclePath({ id: lead.carId })}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline"
                        >
                          View Listing
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                    <div className="text-gray-600">
                      {lead.mestimate != null
                        ? `$${Number(lead.mestimate).toLocaleString()}`
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CHAT TAB */}
            {currentTab === "chat" && (
              <>
                {/* Send Chat Link card */}
                <div className="bg-white rounded-xl border p-4 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="text-sm text-gray-600">Email chat link to the buyer</div>
                      <div className="text-xs text-gray-500">
                        Reuses the existing public chat link for this lead.
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <input
                        type="email"
                        value={overrideEmail}
                        onChange={(e) => setOverrideEmail(e.target.value)}
                        placeholder={lead?.buyerEmail || "Recipient email"}
                        className="h-10 px-3 rounded-lg border border-gray-300 w-full sm:w-64"
                      />
                      <button
                        onClick={sendChatLinkEmail}
                        disabled={sendBusy || (!overrideEmail && !lead?.buyerEmail)}
                        className="h-10 px-3 rounded-lg bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700"
                        title={
                          lead?.buyerEmail
                            ? `Will send to ${overrideEmail || lead.buyerEmail}`
                            : overrideEmail
                            ? `Will send to ${overrideEmail}`
                            : "No recipient email on file"
                        }
                      >
                        {sendBusy ? "Sending…" : "Send Chat Link"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-3">
                    {sendOk && (
                      <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                        Email sent
                      </span>
                    )}
                    {sendErr && (
                      <span className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                        {sendErr}
                      </span>
                    )}
                    {!lead?.buyerEmail && !overrideEmail && (
                      <span className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                        No email on file. Enter one above.
                      </span>
                    )}
                  </div>
                </div>

                {/* Conversation card */}
                <div className="bg-white rounded-xl border p-4 mb-4">
                  <h2 className="text-lg font-medium mb-3">Conversation</h2>

                  {/* Scrollable container like PublicChat */}
                  <div
                    ref={listRef}
                    className="bg-white border rounded-xl h-[60vh] overflow-y-auto p-3 space-y-2"
                    aria-live="polite"
                  >
                    {messages.length === 0 && (
                      <div className="text-sm text-gray-500">No messages yet.</div>
                    )}

                    {messages.map((m) => {
                      const isDealer = m.sender === "dealer";
                      const at = new Date(m.createdAt);
                      const time = isNaN(at)
                        ? ""
                        : at.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          });

                      const status = isDealer ? (m.readAt ? "✔✔ Read" : "✔ Sent") : "";

                      return (
                        <div
                          key={m.id || m.createdAt}
                          className={`flex ${isDealer ? "justify-end" : "justify-start"}`}
                          title={isNaN(at) ? "" : at.toLocaleString()}
                        >
                          <div
                            className={`px-3 py-2 rounded-2xl max-w-[75%] ${
                              isDealer ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-900"
                            }`}
                          >
                            <div className="whitespace-pre-wrap break-words">{m.body}</div>
                            <div className="mt-1 text-[10px] opacity-75 text-right flex gap-2 justify-end">
                              <span>{time}</span>
                              {isDealer && <span>{status}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reply box */}
                  <div className="mt-4">
                    <label className="block text-sm text-gray-700 mb-1">Reply</label>
                    <textarea
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Type your message…"
                      autoFocus={autoFocusReply}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={sendReply}
                        disabled={!reply.trim()}
                        className="px-4 h-10 rounded-lg bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700"
                      >
                        Send
                      </button>
                      <button
                        onClick={() => setReply("")}
                        className="px-4 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
